import React, { useState } from 'react';
import { Lock, Zap, AlertTriangle, Check, Gift, ChevronDown, Calendar } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ScrollFadeIn } from './ScrollFadeIn';
import { ExitIntentPopup } from './ExitIntentPopup';
interface FinalCTAProps {
  onAddToCart?: () => void;
}
export function FinalCTA({
  onAddToCart
}: FinalCTAProps) {
  const [showBonusDetails, setShowBonusDetails] = useState(false);
  const [bump1, setBump1] = useState(false);
  const [bump2, setBump2] = useState(false);
  const [showExitPopup, setShowExitPopup] = useState(false);
  const handleCheckoutClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // If neither bump is selected, intercept and show popup
    if (!bump1 && !bump2) {
      e.preventDefault();
      setShowExitPopup(true);
    }
    // Otherwise let the normal href navigation happen
  };
  const handleAcceptBundle = () => {
    if (onAddToCart) onAddToCart();
    window.location.href = 'https://recursosencalma.com/cart/45866760437933:1';
  };
  const handleDeclineBundle = () => {
    if (onAddToCart) onAddToCart();
    window.location.href = 'https://recursosencalma.com/cart/45769486663853:1';
  };
  return <>
      <section className="scroll-mt-8 py-10 bg-slate-900 relative overflow-hidden" data-id="element-287">
        {/* Background glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-3xl h-96 bg-amber-500/5 blur-[120px] rounded-full pointer-events-none" data-id="element-288" />

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-289">
          <ScrollFadeIn data-id="element-290">
            <h2 className="text-4xl md:text-5xl font-serif text-slate-50 mb-6" data-id="element-291">
              Stop Fighting Your Biology.
              <br data-id="element-292" />
              <span className="text-amber-500 italic" data-id="element-293">
                Start Switching It Off.
              </span>
            </h2>
          </ScrollFadeIn>

          <ScrollFadeIn delay={0.1} data-id="element-294">
            <div className="space-y-4 text-lg text-slate-300 leading-relaxed mb-4" data-id="element-295">
              <p data-id="element-296">
                Everything above — the manual, the kit, the map, and the taper
                plan — is available right now for a single payment of $6.39.
              </p>
              <p data-id="element-297">No subscriptions. No upsells. No "premium tier."</p>
              <p data-id="element-298">
                Just the complete system, delivered instantly to your inbox the
                moment you check out.
              </p>
            </div>
          </ScrollFadeIn>

          {/* Urgency warning */}
          <ScrollFadeIn delay={0.15} data-id="element-299">
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-6 mb-6 text-left" data-id="element-300">
              <div className="flex items-start gap-3" data-id="element-301">
                <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" data-id="element-302" />
                <div className="text-sm text-slate-300 leading-relaxed space-y-2" data-id="element-303">
                  <p className="text-slate-50 font-medium" data-id="element-304">
                    One thing to keep in mind:
                  </p>
                  <p data-id="element-305">
                    The bonus stack is included at this price as part of a
                    limited launch period. Once the launch window closes, the
                    bonuses will either be sold separately or removed from the
                    offer entirely.
                  </p>
                  <p className="text-amber-400 font-medium" data-id="element-306">
                    If you're reading this page, the offer is still active.
                  </p>
                </div>
              </div>
            </div>
          </ScrollFadeIn>

          <ScrollFadeIn delay={0.2} data-id="element-307">
            <div id="final-cta" className="scroll-mt-4 bg-slate-800 border border-slate-700 rounded-2xl p-8 md:p-12 shadow-2xl" data-id="element-308">
              <div className="mb-6" data-id="element-309">
                <span className="text-slate-400 uppercase tracking-widest text-sm font-semibold" data-id="element-310">
                  Digital Access
                </span>
                <div className="mt-3 flex items-center justify-center gap-3" data-id="element-311">
                  <span className="text-2xl text-slate-500 line-through font-medium" data-id="element-312">
                    ${148 + (bump1 ? 9 : 0) + (bump2 ? 9 : 0)}
                  </span>
                  <motion.span key={`${bump1}-${bump2}`} initial={{
                  scale: 1
                }} animate={{
                  scale: [1, 1.08, 1]
                }} transition={{
                  duration: 0.35
                }} className="text-5xl font-bold text-slate-50" data-id="element-313">
                    ${(6.39 + (bump1 ? 9 : 0) + (bump2 ? 9 : 0)).toFixed(2)}
                  </motion.span>
                </div>
                <div className="mt-3 inline-flex items-center px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30" data-id="element-314">
                  <span className="text-emerald-400 text-sm font-semibold tracking-wide" data-id="element-315">
                    {Math.round((1 - (6.39 + (bump1 ? 9 : 0) + (bump2 ? 9 : 0)) / (148 + (bump1 ? 9 : 0) + (bump2 ? 9 : 0))) * 100)}
                    % OFF — Launch Price
                  </span>
                </div>
              </div>

              {/* Bonus stack visual */}
              <div className="border-t border-slate-700 pt-6 mb-6" data-id="element-316">
                <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold mb-4" data-id="element-317">
                  What's included:
                </p>

                {/* Main eBook — prominent */}
                <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-900/60 border border-amber-500/20 mb-4" data-id="element-318">
                  <img src="https://cdn.magicpatterns.com/uploads/7wUxyExPymVP8TFY7ndcVW/The-Cortisol-Switch-Logo.png" alt="The Cortisol Switch eBook" className="w-20 h-auto rounded-lg shadow-md shrink-0" data-id="element-319" />
                  <div className="text-left" data-id="element-320">
                    <span className="text-sm text-slate-50 font-semibold" data-id="element-321">
                      The Cortisol Switch
                    </span>
                    <p className="text-xs text-slate-400" data-id="element-322">
                      Complete eBook — The full protocol
                    </p>
                  </div>
                </div>

                {/* Bonuses — row below */}
                <div className="grid grid-cols-3 gap-3" data-id="element-323">
                  {/* Bonus 1 */}
                  <div className="flex flex-col items-center gap-2 p-3 rounded-xl bg-slate-900/60 border border-slate-700/50" data-id="element-324">
                    <img src="https://cdn.magicpatterns.com/uploads/rcZcghPkokYckJZzRYhPcJ/3AM-Rescue-Kit.png" alt="The 3AM Rescue Kit" className="w-14 h-auto rounded shadow-md" data-id="element-325" />
                    <span className="text-xs text-slate-50 font-medium text-center leading-tight" data-id="element-326">
                      3AM Rescue Kit
                    </span>
                    <span className="text-[10px] text-emerald-400 font-semibold flex items-center gap-1" data-id="element-327">
                      <Gift className="w-3 h-3" data-id="element-328" /> FREE
                    </span>
                  </div>

                  {/* Bonus 2 */}
                  <div className="flex flex-col items-center gap-2 p-3 rounded-xl bg-slate-900/60 border border-slate-700/50" data-id="element-329">
                    <img src="https://cdn.magicpatterns.com/uploads/aDH1GgafDem32o1ZEft9G8/Cortisol-Safe-Grocery-Map.png" alt="Cortisol-Safe Grocery Map" className="w-14 h-auto rounded shadow-md" data-id="element-330" />
                    <span className="text-xs text-slate-50 font-medium text-center leading-tight" data-id="element-331">
                      Grocery Map
                    </span>
                    <span className="text-[10px] text-emerald-400 font-semibold flex items-center gap-1" data-id="element-332">
                      <Gift className="w-3 h-3" data-id="element-333" /> FREE
                    </span>
                  </div>

                  {/* Bonus 3 */}
                  <div className="flex flex-col items-center gap-2 p-3 rounded-xl bg-slate-900/60 border border-slate-700/50" data-id="element-334">
                    <img src="https://cdn.magicpatterns.com/uploads/cuSNPLuFnNdPTDBYMEMsKM/Coffee-Exit-Plan.png" alt="The Coffee Exit Plan" className="w-14 h-auto rounded shadow-md" data-id="element-335" />
                    <span className="text-xs text-slate-50 font-medium text-center leading-tight" data-id="element-336">
                      Coffee Exit Plan
                    </span>
                    <span className="text-[10px] text-emerald-400 font-semibold flex items-center gap-1" data-id="element-337">
                      <Gift className="w-3 h-3" data-id="element-338" /> FREE
                    </span>
                  </div>
                </div>

                {/* Read more toggle */}
                <button onClick={() => setShowBonusDetails(!showBonusDetails)} className="mt-4 inline-flex items-center gap-1.5 text-sm text-amber-400 hover:text-amber-300 transition-colors font-medium" data-id="element-339">
                  {showBonusDetails ? 'Show less' : 'Read more about the bonuses'}
                  <motion.span animate={{
                  rotate: showBonusDetails ? 180 : 0
                }} transition={{
                  duration: 0.3
                }} className="inline-flex" data-id="element-340">
                    <ChevronDown className="w-4 h-4" data-id="element-341" />
                  </motion.span>
                </button>

                {/* Expandable bonus details */}
                <AnimatePresence data-id="element-342">
                  {showBonusDetails && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.4,
                  ease: [0.21, 0.47, 0.32, 0.98]
                }} className="overflow-hidden" data-id="element-343">
                      <div className="pt-6 space-y-6 text-left" data-id="element-344">
                        {/* Bonus 1 — 3AM Rescue Kit */}
                        <div className="bg-slate-900/60 border border-slate-700/50 rounded-xl p-5 space-y-4" data-id="element-345">
                          <div data-id="element-346">
                            <div className="inline-flex items-center gap-2 text-amber-500 text-xs font-semibold uppercase tracking-widest mb-2" data-id="element-347">
                              <Gift className="w-3.5 h-3.5" data-id="element-348" />
                              <span data-id="element-349">Bonus #1</span>
                            </div>
                            <h4 className="text-lg font-serif text-slate-50 mb-1" data-id="element-350">
                              The 3AM Rescue Kit
                            </h4>
                            <p className="text-slate-400 text-xs" data-id="element-351">
                              Fast-Back-to-Sleep Protocol —{' '}
                              <span className="text-emerald-400 font-semibold" data-id="element-352">
                                FREE
                              </span>
                            </p>
                          </div>
                          <p className="text-slate-300 text-sm leading-relaxed" data-id="element-353">
                            A decision-based rapid-response system for when you
                            wake at 3AM. Instead of guessing what to do
                            half-asleep, follow a simple 3-route decision tree:
                          </p>
                          <div className="space-y-2" data-id="element-354">
                            <div className="flex items-start gap-2.5" data-id="element-355">
                              <div className="w-4 h-4 rounded-full bg-blue-400 shrink-0 mt-0.5" data-id="element-356" />
                              <p className="text-slate-300 text-xs" data-id="element-357">
                                <span className="text-slate-50 font-medium" data-id="element-358">
                                  Route A:
                                </span>{' '}
                                Heart racing — 3-step physical reset
                              </p>
                            </div>
                            <div className="flex items-start gap-2.5" data-id="element-359">
                              <div className="w-4 h-4 rounded-full bg-amber-400 shrink-0 mt-0.5" data-id="element-360" />
                              <p className="text-slate-300 text-xs" data-id="element-361">
                                <span className="text-slate-50 font-medium" data-id="element-362">
                                  Route B:
                                </span>{' '}
                                Real hunger — rescue snack rules
                              </p>
                            </div>
                            <div className="flex items-start gap-2.5" data-id="element-363">
                              <div className="w-4 h-4 rounded-full bg-emerald-400 shrink-0 mt-0.5" data-id="element-364" />
                              <p className="text-slate-300 text-xs" data-id="element-365">
                                <span className="text-slate-50 font-medium" data-id="element-366">
                                  Route C:
                                </span>{' '}
                                Mind looping — 4-line journal script
                              </p>
                            </div>
                          </div>
                          <div className="space-y-1.5" data-id="element-367">
                            {['60-second decision tree (usable half-asleep)', '4-7-8 Vagal Brake sequence', 'Rescue snack rules', 'Anti-rumination script', '7 mistakes that keep you awake', 'Printable nightstand one-pager'].map(item => <div key={item} className="flex items-start gap-2" data-id="element-368">
                                <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" data-id="element-369" />
                                <span className="text-slate-300 text-xs" data-id="element-370">
                                  {item}
                                </span>
                              </div>)}
                          </div>
                        </div>

                        {/* Bonus 2 — Grocery Map */}
                        <div className="bg-slate-900/60 border border-slate-700/50 rounded-xl p-5 space-y-4" data-id="element-371">
                          <div data-id="element-372">
                            <div className="inline-flex items-center gap-2 text-amber-500 text-xs font-semibold uppercase tracking-widest mb-2" data-id="element-373">
                              <Gift className="w-3.5 h-3.5" data-id="element-374" />
                              <span data-id="element-375">Bonus #2</span>
                            </div>
                            <h4 className="text-lg font-serif text-slate-50 mb-1" data-id="element-376">
                              Cortisol-Safe Grocery Map
                            </h4>
                            <p className="text-slate-400 text-xs" data-id="element-377">
                              Done-for-You Shopping List + Night Staples (14
                              Days) —{' '}
                              <span className="text-emerald-400 font-semibold" data-id="element-378">
                                FREE
                              </span>
                            </p>
                          </div>
                          <p className="text-slate-300 text-sm leading-relaxed" data-id="element-379">
                            A shopping system — not a meal plan. One trip to the
                            store sets you up for two full weeks of calmer
                            evenings and steadier sleep.
                          </p>
                          <div className="space-y-1.5" data-id="element-380">
                            {['12 Night Staples for blood sugar stability', 'Aisle-by-aisle shopping list', 'Cortisol-Safe Dinner Formula', '14 simple dinner + snack combos (10-15 min)', 'Dairy-free, gluten-free & vegetarian swaps', '"Better to Avoid" trigger food list', '"Your First Cart" fill-in template'].map(item => <div key={item} className="flex items-start gap-2" data-id="element-381">
                                <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" data-id="element-382" />
                                <span className="text-slate-300 text-xs" data-id="element-383">
                                  {item}
                                </span>
                              </div>)}
                          </div>
                        </div>

                        {/* Bonus 3 — Coffee Exit Plan */}
                        <div className="bg-slate-900/60 border border-slate-700/50 rounded-xl p-5 space-y-4" data-id="element-384">
                          <div data-id="element-385">
                            <div className="inline-flex items-center gap-2 text-amber-500 text-xs font-semibold uppercase tracking-widest mb-2" data-id="element-386">
                              <Gift className="w-3.5 h-3.5" data-id="element-387" />
                              <span data-id="element-388">Bonus #3</span>
                            </div>
                            <h4 className="text-lg font-serif text-slate-50 mb-1" data-id="element-389">
                              The Coffee Exit Plan
                            </h4>
                            <p className="text-slate-400 text-xs" data-id="element-390">
                              2-Week Caffeine Taper Without the Crash —{' '}
                              <span className="text-emerald-400 font-semibold" data-id="element-391">
                                FREE
                              </span>
                            </p>
                          </div>
                          <p className="text-slate-300 text-sm leading-relaxed" data-id="element-392">
                            Breaks the caffeine-cortisol loop without quitting
                            cold turkey. 14 days, 3 simple phases:
                          </p>
                          <div className="space-y-2" data-id="element-393">
                            <div className="flex items-start gap-2.5" data-id="element-394">
                              <Calendar className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" data-id="element-395" />
                              <p className="text-slate-300 text-xs" data-id="element-396">
                                <span className="text-slate-50 font-medium" data-id="element-397">
                                  Days 1-3:
                                </span>{' '}
                                Timing reset
                              </p>
                            </div>
                            <div className="flex items-start gap-2.5" data-id="element-398">
                              <Calendar className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" data-id="element-399" />
                              <p className="text-slate-300 text-xs" data-id="element-400">
                                <span className="text-slate-50 font-medium" data-id="element-401">
                                  Days 4-7:
                                </span>{' '}
                                Gentle taper
                              </p>
                            </div>
                            <div className="flex items-start gap-2.5" data-id="element-402">
                              <Calendar className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" data-id="element-403" />
                              <p className="text-slate-300 text-xs" data-id="element-404">
                                <span className="text-slate-50 font-medium" data-id="element-405">
                                  Days 8-14:
                                </span>{' '}
                                Stabilize
                              </p>
                            </div>
                          </div>
                          <div className="space-y-1.5" data-id="element-406">
                            {['4 Timing Rules for jitters & crashes', 'Cut-off time calculator', '10 non-caffeine energy boosters', '2PM Crash Protocol (5 min fix)', 'Headache & irritability troubleshooting', '14-day daily tracker'].map(item => <div key={item} className="flex items-start gap-2" data-id="element-407">
                                <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" data-id="element-408" />
                                <span className="text-slate-300 text-xs" data-id="element-409">
                                  {item}
                                </span>
                              </div>)}
                          </div>
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>

              {/* Confirmation line */}
              <div className="flex items-center justify-center gap-2 mb-6" data-id="element-410">
                <Check className="w-5 h-5 text-emerald-400" data-id="element-411" />
                <span className="text-emerald-400 font-semibold" data-id="element-412">
                  YES — I want The Cortisol Switch + all 3 bonuses
                </span>
              </div>

              <a href={(() => {
              // Base product only (no bumps)
              if (!bump1 && !bump2) {
                return 'https://www.recursosencalma.com/cart/45769486663853:1';
              }
              // Base + Bump 1 only
              if (bump1 && !bump2) {
                return 'https://recursosencalma.com/cart/45844404961453:1';
              }
              // Base + Bump 2 only
              if (!bump1 && bump2) {
                return 'https://recursosencalma.com/cart/45844411449517:1';
              }
              // Base + Both bumps
              if (bump1 && bump2) {
                return 'https://recursosencalma.com/cart/45844417872045:1';
              }
              // Fallback
              return 'https://www.recursosencalma.com/cart/45769486663853:1';
            })()} target="_blank" rel="noopener noreferrer" onClick={e => {
              handleCheckoutClick(e);
              if (bump1 || bump2) {
                if (onAddToCart) onAddToCart();
              }
            }} className="cta-pulse w-full sm:w-auto inline-flex items-center justify-center px-10 py-5 text-xl font-bold text-slate-900 bg-amber-500 rounded-xl hover:bg-amber-400 transition-colors duration-300 mb-4" data-id="element-413">
                Download The Cortisol Switch Now
              </a>

              <p className="text-sm text-slate-300 mb-6 flex items-center justify-center gap-2" data-id="element-414">
                <span className="text-amber-400 text-lg" data-id="element-415">👇</span>
                <span className="italic font-medium" data-id="element-416">
                  Want faster results? Check out these exclusive add‑ons below
                </span>
                <span className="text-amber-400 text-lg" data-id="element-417">👇</span>
              </p>

              {/* Highly Recommended badge */}
              <div className="flex items-center justify-center gap-2 mb-3" data-id="element-418">
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-500/30 to-transparent" data-id="element-419" />
                <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-amber-400 px-3 py-1 rounded-full border border-amber-500/25 bg-amber-500/5" data-id="element-420">
                  ⭐ Highly Recommended
                </span>
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-500/30 to-transparent" data-id="element-421" />
              </div>

              {/* ORDER BUMP #1 */}
              <div onClick={() => setBump1(!bump1)} className={`bump-shimmer text-left rounded-xl p-5 mb-4 cursor-pointer transition-all duration-300 border-2 ${bump1 ? 'border-amber-500 bg-amber-500/5 shadow-[0_0_20px_rgba(245,158,11,0.1)]' : 'border-amber-500/30 bg-slate-900/60 hover:border-amber-500/60'}`} data-id="element-422">
                <div className="flex items-start gap-4" data-id="element-423">
                  <div className="shrink-0 mt-1" data-id="element-424">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${bump1 ? 'bg-amber-500 border-amber-500' : 'border-slate-500'}`} data-id="element-425">
                      {bump1 && <Check className="w-3.5 h-3.5 text-slate-900" data-id="element-426" />}
                    </div>
                  </div>
                  <div className="flex-1" data-id="element-427">
                    <div className="flex items-start gap-3 mb-3" data-id="element-428">
                      <img src="https://cdn.magicpatterns.com/uploads/fxsWhHWinb4ud5Fwv8s2as/The-Cortisol-Elite-Motion-Bible.png" alt="The Cortisol-Safe Elite Motion Bible" className="w-16 h-auto rounded-lg shadow-md shrink-0" data-id="element-429" />
                      <div data-id="element-430">
                        <p className="text-sm text-slate-50 font-bold leading-snug" data-id="element-431">
                          The Cortisol‑Safe Elite Motion Bible
                        </p>
                        <p className="text-xs text-slate-400 mt-0.5" data-id="element-432">
                          Desk Resets + At-Home Sculpt Routine
                        </p>
                        <p className="mt-1" data-id="element-433">
                          <span className="text-lg text-amber-400 font-bold" data-id="element-434">
                            $9
                          </span>{' '}
                          <span className="text-emerald-400 text-xs font-semibold" data-id="element-435">
                            (Save 90%)
                          </span>
                        </p>
                      </div>
                    </div>
                    <div className="space-y-2 text-xs text-slate-400 leading-relaxed" data-id="element-436">
                      <p data-id="element-437">
                        Stop "punishing" your body with high-intensity workouts
                        that only spike your cortisol and keep your belly fat
                        locked in place. Most people over 40 are accidentally
                        over-stressing their hearts and glands in the gym.
                      </p>
                      <p data-id="element-438">
                        Inside this Elite Playbook, you'll discover the 2-minute
                        "Desk-Resets" to drain your lymphatic system during a
                        conference call, plus a specialized At-Home "Sculpt"
                        Routine that builds lean muscle without triggering the
                        stress-response.
                      </p>
                      <p className="text-slate-300" data-id="element-439">
                        Learn how to move with intensity while keeping your
                        nervous system in a state of "Total Calm." Get the body
                        without the burnout for less than a movie ticket.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Highly Recommended badge for bump #2 */}
              <div className="flex items-center justify-center gap-2 mb-3" data-id="element-440">
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-500/30 to-transparent" data-id="element-441" />
                <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-amber-400 px-3 py-1 rounded-full border border-amber-500/25 bg-amber-500/5" data-id="element-442">
                  ⭐ Highly Recommended
                </span>
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-500/30 to-transparent" data-id="element-443" />
              </div>

              {/* ORDER BUMP #2 */}
              <div onClick={() => setBump2(!bump2)} className={`bump-shimmer text-left rounded-xl p-5 mb-6 cursor-pointer transition-all duration-300 border-2 ${bump2 ? 'border-amber-500 bg-amber-500/5 shadow-[0_0_20px_rgba(245,158,11,0.1)]' : 'border-amber-500/30 bg-slate-900/60 hover:border-amber-500/60'}`} data-id="element-444">
                <div className="flex items-start gap-4" data-id="element-445">
                  <div className="shrink-0 mt-1" data-id="element-446">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${bump2 ? 'bg-amber-500 border-amber-500' : 'border-slate-500'}`} data-id="element-447">
                      {bump2 && <Check className="w-3.5 h-3.5 text-slate-900" data-id="element-448" />}
                    </div>
                  </div>
                  <div className="flex-1" data-id="element-449">
                    <div className="flex items-start gap-3 mb-3" data-id="element-450">
                      <img src="https://cdn.magicpatterns.com/uploads/1WsdHs7Nz6EJrKhZPEVY83/Stress-Proof-Dining-Out-Guide.png" alt="Stress-Proof Dining Out Guide" className="w-16 h-auto rounded-lg shadow-md shrink-0" data-id="element-451" />
                      <div data-id="element-452">
                        <p className="text-sm text-slate-50 font-bold leading-snug" data-id="element-453">
                          Stress‑Proof Dining Out Guide
                        </p>
                        <p className="text-xs text-slate-400 mt-0.5" data-id="element-454">
                          Safe Orders + Social Buffer Strategy
                        </p>
                        <p className="mt-1" data-id="element-455">
                          <span className="text-lg text-amber-400 font-bold" data-id="element-456">
                            $9
                          </span>{' '}
                          <span className="text-amber-400 text-xs font-semibold" data-id="element-457">
                            (One‑time offer)
                          </span>
                        </p>
                      </div>
                    </div>
                    <div className="space-y-2 text-xs text-slate-400 leading-relaxed" data-id="element-458">
                      <p data-id="element-459">
                        Life doesn't pause while you're resetting your
                        metabolism—and you shouldn't have to fear the menu.
                        Whether it's a business dinner, a weekend celebration,
                        or an airport layover, this guide helps you stay in
                        "Fat-Burning Mode" without being the "difficult person"
                        at the table.
                      </p>
                      <p data-id="element-460">
                        This Mobile-Optimized Pocket Guide gives you elegant
                        "Safe-Orders" for Italian, Mexican, Steakhouses, and
                        even Fast-Casual spots—plus the "Social Buffer" strategy
                        for enjoying a drink without triggering the dreaded 3 AM
                        wake-up call later.
                      </p>
                      <p className="text-slate-300" data-id="element-461">
                        Keep your social life AND your results. Consider this
                        your biological insurance policy for the real world.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-[11px] text-amber-500/70 font-medium mb-4 italic" data-id="element-462">
                One-time offer. This price is only available now.
              </p>

              {/* DYNAMIC ORDER SUMMARY */}
              <div className="rounded-xl border border-slate-700 bg-slate-900/80 p-5 mb-6 text-left" data-id="element-463">
                <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold mb-3" data-id="element-464">
                  🛒 Your Order Summary
                </p>
                <div className="space-y-2" data-id="element-465">
                  {/* Base product - always included */}
                  <div className="flex items-center justify-between text-sm" data-id="element-466">
                    <span className="text-slate-300" data-id="element-467">
                      The Cortisol Switch + 3 Bonuses
                    </span>
                    <span className="text-slate-50 font-semibold" data-id="element-468">$6.39</span>
                  </div>

                  {/* Bump 1 */}
                  <AnimatePresence data-id="element-469">
                    {bump1 && <motion.div initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} exit={{
                    opacity: 0,
                    height: 0
                  }} transition={{
                    duration: 0.2
                  }} className="flex items-center justify-between text-sm overflow-hidden" data-id="element-470">
                        <span className="text-slate-300" data-id="element-471">
                          Elite Motion Bible
                        </span>
                        <span className="text-amber-400 font-semibold" data-id="element-472">
                          $9.00
                        </span>
                      </motion.div>}
                  </AnimatePresence>

                  {/* Bump 2 */}
                  <AnimatePresence data-id="element-473">
                    {bump2 && <motion.div initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} exit={{
                    opacity: 0,
                    height: 0
                  }} transition={{
                    duration: 0.2
                  }} className="flex items-center justify-between text-sm overflow-hidden" data-id="element-474">
                        <span className="text-slate-300" data-id="element-475">Dining Out Guide</span>
                        <span className="text-amber-400 font-semibold" data-id="element-476">
                          $9.00
                        </span>
                      </motion.div>}
                  </AnimatePresence>

                  {/* Divider */}
                  <div className="border-t border-slate-700 pt-2 mt-2 flex items-center justify-between" data-id="element-477">
                    <span className="text-slate-50 font-bold text-sm" data-id="element-478">
                      Total
                    </span>
                    <motion.span key={`summary-${bump1}-${bump2}`} initial={{
                    scale: 1
                  }} animate={{
                    scale: [1, 1.1, 1]
                  }} transition={{
                    duration: 0.3
                  }} className="text-xl font-bold text-amber-400" data-id="element-479">
                      ${(6.39 + (bump1 ? 9 : 0) + (bump2 ? 9 : 0)).toFixed(2)}
                    </motion.span>
                  </div>
                </div>

                {/* Dynamic Buy Button */}
                <a href={(() => {
                // Base product only (no bumps)
                if (!bump1 && !bump2) {
                  return 'https://www.recursosencalma.com/cart/45769486663853:1';
                }
                // Base + Bump 1 only
                if (bump1 && !bump2) {
                  return 'https://recursosencalma.com/cart/45844404961453:1';
                }
                // Base + Bump 2 only
                if (!bump1 && bump2) {
                  return 'https://recursosencalma.com/cart/45844411449517:1';
                }
                // Base + Both bumps
                if (bump1 && bump2) {
                  return 'https://recursosencalma.com/cart/45844417872045:1';
                }
                // Fallback (should never reach here)
                return 'https://www.recursosencalma.com/cart/45769486663853:1';
              })()} target="_blank" rel="noopener noreferrer" onClick={e => {
                handleCheckoutClick(e);
                if (bump1 || bump2) {
                  if (onAddToCart) onAddToCart();
                }
              }} className="cta-pulse mt-4 w-full inline-flex items-center justify-center px-8 py-4 text-lg font-bold text-slate-900 bg-amber-500 rounded-xl hover:bg-amber-400 transition-colors duration-300 text-center" data-id="element-480">
                  Download The Cortisol Switch
                </a>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-6 text-sm text-slate-400 mb-4" data-id="element-481">
                <div className="flex items-center" data-id="element-482">
                  <Lock className="w-4 h-4 mr-2 text-emerald-400" data-id="element-483" />
                  Secure Checkout
                </div>
                <div className="hidden sm:block text-slate-600" data-id="element-484">•</div>
                <div className="flex items-center" data-id="element-485">
                  <Zap className="w-4 h-4 mr-2 text-amber-400" data-id="element-486" />
                  Immediate Digital Access
                </div>
              </div>

              <p className="text-xs text-slate-500" data-id="element-487">
                Secure checkout. Immediate digital delivery. 100% satisfaction
                guaranteed.
              </p>
            </div>
          </ScrollFadeIn>
        </div>
      </section>

      <ExitIntentPopup isOpen={showExitPopup} onClose={() => setShowExitPopup(false)} onAccept={handleAcceptBundle} onDecline={handleDeclineBundle} data-id="element-488" />
    </>;
}